import { useResponse } from 'contexts/response';
import { useCallback } from 'react';

import { Customer, Page, TruckingCompany } from 'types';
import { formatTruckingCompany } from 'utils/format';

interface FetchDataParams {
  pageNum?: number;
  pageSize?: number;
  filterString?: string;
  sortField?: string;
  sortDirection?: string;
}

interface ItiBaseEntity {
  createdBy: string;
  createdDate: Date | number | string;
  lastUpdatedBy?: string | null;
  lastUpdatedDate?: Date | number | string | null;
}

export interface CustomerCarrier extends ItiBaseEntity {
  id: number;
  customer: Customer;
  truckingCompany: TruckingCompany;
  gateInAllowed: boolean;
  gateOutAllowed: boolean;
  startDate?: string | number | null;
  notes?: string | null;
}

const isCustomerCarrier = (json: any): json is CustomerCarrier => {
  return (
    typeof json === 'object' &&
    typeof json.customer === 'object' &&
    typeof json.truckingCompany === 'object' &&
    typeof json.gateInAllowed === 'boolean' &&
    typeof json.gateOutAllowed === 'boolean'
  );
};

const isCustomerCarrierPage = (json: any): json is Page<CustomerCarrier> => {
  return typeof json === 'object' && 'content' in json && Array.isArray(json.content);
};

const useCustomerCarrierApi = () => {
  const { handleError, setSuccess } = useResponse();

  const fetchData = useCallback(async (args: FetchDataParams): Promise<Page<CustomerCarrier>> => {
    const init: RequestInit = {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
    };

    const input = new URL('/api/customer-carriers', window.location.origin);
    Object.entries(args).forEach(([param, value]) => {
      input.searchParams.append(param, `${value}`);
    });

    try {
      const response = await fetch(input, init);
      if (response.ok) {
        const json = await response.json();
        if (isCustomerCarrierPage(json)) {
          return json;
        }
      }
      return { content: [] };
    } catch (_) {
      return { content: [] };
    }
  }, []);

  const handleJsonResponse = useCallback(
    async (input: RequestInfo | URL, init: RequestInit) => {
      try {
        const response = await fetch(input, init);
        const json = await response.json();
        if (response.ok && isCustomerCarrier(json)) {
          setSuccess(
            <dl>
              <dt className="text-info-main">ACCOUNT</dt>
              <dd className="text-info-dark">{json.customer?.name}</dd>
              <dt className="text-info-main">CARRIER</dt>
              <dd className="text-info-dark">{formatTruckingCompany(json.truckingCompany)}</dd>
              <dt className="text-info-main">GATE IN ALLOWED</dt>
              <dd className="text-info-dark">{json.gateInAllowed ? 'YES' : 'NO'}</dd>
              <dt className="text-info-main">GATE OUT ALLOWED</dt>
              <dd className="text-info-dark">{json.gateOutAllowed ? 'YES' : 'NO'}</dd>
            </dl>
          );
          return json;
        }
        handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
        return null;
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const createRule = useCallback(
    async (rule: Partial<CustomerCarrier>): Promise<CustomerCarrier | null> => {
      if (!isCustomerCarrier(rule)) {
        handleError('Invalid rule');
        return null;
      }
      const init: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(rule)
      };
      const input = new URL('/api/customer-carriers', window.location.origin);
      return await handleJsonResponse(input, init);
    },
    [handleError, handleJsonResponse]
  );

  const editRule = useCallback(
    async (rule?: CustomerCarrier | null): Promise<CustomerCarrier | null> => {
      if (!rule?.id) {
        handleError('Invalid rule');
        return null;
      }
      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(rule)
      };
      const input = new URL(`/api/customer-carriers/${rule.id}`, window.location.origin);
      return await handleJsonResponse(input, init);
    },
    [handleError, handleJsonResponse]
  );

  const deleteRule = useCallback(
    async (id: number) => {
      const init: RequestInit = {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const input = new URL(`/api/customer-carriers/${id}`, window.location.origin);

      try {
        const response = await fetch(input, init);
        if (response.ok) {
          setSuccess(`Rule #${id} deleted`);
          return;
        }
        const json = await response.json();
        handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
      } catch (e) {
        handleError(e);
      }
    },
    [handleError, setSuccess]
  );

  const getRule = useCallback(
    async (id: number | string): Promise<CustomerCarrier | null> => {
      const init: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const input = new URL(`/api/customer-carriers/${id}`, window.location.origin);

      try {
        const response = await fetch(input, init);
        const json = await response.json();
        if (response.ok && isCustomerCarrier(json)) {
          return json;
        }
        handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
        return null;
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError]
  );

  return { createRule, deleteRule, editRule, fetchData, getRule };
};

export default useCustomerCarrierApi;
